import { Injectable, EventEmitter } from "@angular/core";
import { AuthenticationService } from "./auth.service";
import { UserModel } from "../model/user.model";
import { Observable, EMPTY } from "rxjs";
import { GlobalSettings } from "../model/settings.model";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AttributeModel } from "../main/dynamic/model/attribute.model";
import { WorkspaceModel } from "../model/workspace.model";

@Injectable()
export class UserService {

    requestChanged: EventEmitter<boolean> = new EventEmitter();
    private users: UserModel[] = []
    private authenticatedUser: UserModel

    constructor(
        private authService: AuthenticationService,
        private settings: GlobalSettings,
        private http: HttpClient) {
    }

    get headers() {
        return new HttpHeaders()
            .set("Content-Type", "application/json")
            .set("Authorization", `Bearer ${this.authService.getToken()}`)
    }

    changePassword(newPassword: string): Observable<any> {
        if(this.authenticatedUser) {
            return this.http.patch<any>(`${this.settings.securityApiUrl}/Users/${this.authenticatedUser.id}/Password`, 
            {
                user: this.authenticatedUser.userName,
                password: newPassword
            },
            { 
                headers : this.headers 
            })
        } else {
            return new Observable<any>((observer) => {
                observer.complete()
            })
        }
    }

    fromJwtToAttribute() : AttributeModel {
        let user = this.authService.getUser()

        return this.fromUserToAttribute(user)
    }

    fromUserToAttribute(userModel: UserModel) : AttributeModel {
        if(userModel) {
            let userAttribute = new AttributeModel()
            userAttribute.key = "user"
            userAttribute.value = userModel.userName
            userAttribute.type = "text"
            
            let attributes: AttributeModel[] = []

            this.addAttribute("name", userModel.name, attributes)

            this.addAttribute("email", userModel.email, attributes)

            for (const roleName of userModel.roles) {
                this.addAttribute("role", roleName, attributes)
            }

            userAttribute.attributes = attributes

            return userAttribute
        }
    }

    private addAttribute(key: string, value: string, attributes: AttributeModel[]): any {
        if(value) {
            let attribute = new AttributeModel()
            attribute.key = key
            attribute.value = value
            attribute.type = "text"
            attributes.push(attribute)
        }
    }

    impersonate(tenantId: string, workspaceId: string): Observable<any> {
        return this.http.get<any>(`${this.settings.securityApiUrl}/Users/Impersonate/Token?tenantId=${tenantId}&workspaceId=${workspaceId}`, { 
            headers : this.headers 
        });
    }

    getWorkspaces(): Observable<WorkspaceModel[]> {
        return this.http.get<WorkspaceModel[]>(`${this.settings.securityApiUrl}/Users/Workspaces`, { 
            headers : this.headers 
        })
    }

    getAllUsers(): Observable<UserModel[]> {
        if(this.users.length > 0) {
            return new Observable<UserModel[]>((observer) => {
                observer.next(this.users)
                observer.complete()
            })
        } else {
            return this.http.get<any>(`${this.settings.securityApiUrl}/Users`, { 
                    headers : this.headers 
                })
                .pipe(
                    map((rawUsers: Array<any>) => {
                        let returnedUsers: UserModel[] = []

                        if(rawUsers && rawUsers.length > 0) {
                            for (const rawUser of rawUsers) {

                                let userName = this.getUserNameByCredential(rawUser.credentials)

                                if(!userName) {
                                    continue;
                                }

                                let user: UserModel = {
                                    id: rawUser.id,
                                    userName : userName,
                                    name : rawUser.name,
                                    email : rawUser.email,
                                    roles : rawUser.roles,
                                    attributes : rawUser.attributes,
                                    hasValidPassword : rawUser.hasValidPassword,
                                    userType: rawUser.userType,
                                    defaultProcessId: "",
                                    inactive: rawUser.inactive
                                }
    
                                returnedUsers.push(user)
                            }
                        }

                        this.users = returnedUsers
    
                        return this.users
                    })
                )
        }
    }

    private getUserNameByCredential(credentials: any[]): any {
        if(credentials.length > 0) {
            return credentials[0].user
        }
    }

    getCurrentUserAttribute(attributeName: string): AttributeModel {
        if(this.authenticatedUser) {
            var attribute = this.authenticatedUser.attributes
                .find(attr => attr.key.toLowerCase() == attributeName.toLowerCase())

            if(attribute) {
                return attribute
            }
        }

        return null
    }

    getCurrentUserInstanceReferences(): AttributeModel[] {
        if(this.authenticatedUser) {
            var attributes = this.authenticatedUser.attributes
                .filter(attr => attr.key.toLowerCase().trim().endsWith("id"));

            if(attributes.length > 0) {
                return attributes;
            }
        }

        return []
    }

    getCurrentUserAttributes(attributeName: string): AttributeModel[] {
        if(this.authenticatedUser) {
            var attribute = this.authenticatedUser.attributes
                .find(attr => attr.key.toLowerCase() == attributeName.toLowerCase())

            if(attribute) {
                return attribute.attributes
            }
        }

        return []
    }

    getUserName(user: UserModel): string {
        let split = user.userName.split("\\")

        return split[split.length-1]
    }

    hasValidPassword(): boolean {
        if(this.authenticatedUser) {
            return this.authenticatedUser.hasValidPassword
        }
        
        return false
    }

    loadUserAttributes(): Observable<any> {
        if(!this.authService.isAuthenticated()) {
            return EMPTY
        }

        let user = this.authService.getUser()

        if(!user) {
            return EMPTY
        }

        localStorage.setItem("tid", user.tenantId)
        localStorage.setItem("wid", user.workspaceId)

        return this.http.get<any>(`${this.settings.securityApiUrl}/Users/${user.id}`, { 
                headers : this.headers 
            })
            .pipe(
                map((rawUser) => {
                    if(rawUser) {
                        this.authenticatedUser = {
                            id: rawUser.id,
                            userName : this.getUserNameByCredential(rawUser.credentials),
                            name : rawUser.name,
                            email : rawUser.email,
                            roles : rawUser.roles,
                            attributes : rawUser.attributes,
                            hasValidPassword : rawUser.hasValidPassword,
                            userType: rawUser.userType,
                            defaultProcessId: ""
                        }
                    }
                })
            )
    }
}